
/**
 * Raw error shape from the API client (string or object with message).
 * Used for originalResponse inside NestBaseQueryError.
 */
export type NestClientError = string | { message?: string; [key: string]: unknown };


/**
 * Error object returned by nestBaseQuery (and thus by RTK Query hooks using it).
 */
export interface NestBaseQueryError {
  message: string;
  status: number;
  originalResponse: NestClientError | Error;
  cause?: string;
}
/**
 * Typed baseQuery for RTK Query using Nest API client
 * Uses generated types from api.ts for full type safety
 * 
 * @template TResponse - The response data type from the API
 */
export const nestBaseQuery = async <TResponse = unknown>(
  clientCall: Promise<{
    data?: TResponse;
    error?: unknown;
    response: Response;
  }>
): Promise<
  | { data: TResponse }
  | {
      error: NestBaseQueryError;
    }
> => {
  try {
    const response = await clientCall;

    if (response.error) {
      const errorMessage =
        typeof response.error === 'string'
          ? response.error
          : (response.error as { message?: string })?.message || 'An error occurred';

      return {
        error: {
          message: errorMessage,
          status: response.response?.status || 500,
          originalResponse: response.error as NestClientError | Error,
        },
      };
    }

    return { data: response.data as TResponse };
  } catch (err: unknown) {
    const error = err as { message?: string };
    const originalResponse = err instanceof Error ? err : (err as NestClientError);
    return {
      error: {
        message: error?.message || 'A network error occurred',
        status: 500,
        originalResponse,
      },
    };
  }
};

